import { motion } from "framer-motion";
import LottieAnimation from "./LottieAnimation";
import { translations } from "../../i18n";

type Translation = (typeof translations)["de"];

function ChallengeSolution({
  isVisible,
  t,
}: {
  isVisible: (id: string) => boolean;
  t?: Translation["problems"];
}) {
  // Function to render HTML content safely
  const renderHTML = (html: string) => {
    return { __html: html };
  };
  return (
    <section
      id="challenge-solution"
      data-animate
      className={`py-20 md:py-32 transition-all duration-1000 ${
        isVisible("challenge-solution")
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-[40%_1fr] gap-8 md:gap-12 lg:gap-16">
          {/* Challenge Column */}
          <div className="flex flex-col items-center">
            {/* Text Content */}
            <div className="text-center mb-6">
              <h3 className="text-2xl md:text-3xl font-bold text-orange-500 mb-4">
                Challenge
              </h3>
              <p
                className="text-slate-400 text-sm md:text-base leading-relaxed max-w-md mx-auto"
                dangerouslySetInnerHTML={renderHTML(t?.description || "Critical information is scattered across multiple sources and portals. <strong><u>Teams rely on manual monitoring, leading to missed insights</u></strong> and delayed decisions.")}
              />
            </div>

            {/* Animated Arrow */}
            <div className="flex flex-col items-center my-4">
              <motion.div
                className="flex flex-col items-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="w-px h-8 bg-gradient-to-b from-slate-600 to-slate-500" />
                <motion.div
                  animate={{ y: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <svg
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    className="text-slate-500"
                  >
                    <path
                      d="M1 1L6 6L11 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </motion.div>
                <div className="w-px h-8 bg-gradient-to-b from-slate-500 to-transparent" />
              </motion.div>
            </div>

            {/* Lottie Animation */}
            <motion.div
              className="w-full h-[300px] md:h-[400px] overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <LottieAnimation path="/problem.json" />
            </motion.div>
          </div>

          {/* Solution Column */}
          <div className="flex flex-col items-center">
            {/* Text Content */}
            <div className="text-center mb-6">
              <h3 className="text-2xl md:text-3xl font-bold text-orange-500 mb-4">
                {t?.solutionHeading || "Solution"}
              </h3>
              <p
                className="text-slate-400 text-sm md:text-base leading-relaxed max-w-md mx-auto"
                dangerouslySetInnerHTML={renderHTML(t?.solutionDescription || "Data from multiple sources is automatically collected and unified in one platform. <strong>Users receive clear, structured insights</strong> that enable faster and more confident decisions.")}
              />
            </div>

            {/* Animated Arrow */}
            <div className="flex flex-col items-center my-4">
              <motion.div
                className="flex flex-col items-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="w-px h-8 bg-gradient-to-b from-slate-600 to-slate-500" />
                <motion.div
                  animate={{ y: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <svg
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    className="text-slate-500"
                  >
                    <path
                      d="M1 1L6 6L11 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </motion.div>
                <div className="w-px h-8 bg-gradient-to-b from-slate-500 to-transparent" />
              </motion.div>
            </div>

            {/* Lottie Animation */}
            <motion.div
              className="w-full h-[300px] md:h-[400px] overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <LottieAnimation path="/solution.json" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ChallengeSolution;